import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { RootState } from '../store';
import { useGetUsersId } from '../api/generated/arrayscapeGamingDashboardAPI';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import CustomerDetailModal from '../components/CustomerDetailModal';
import PaymentDetailModal from '../components/PaymentDetailModal';
import PaymentsTable, { paymentFilterKeys } from '../components/PaymentsTable';
import { useQueryClient } from '@tanstack/react-query';

const PaymentsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [period, setPeriod] = useState<string>(() => searchParams.get('period') || 'year');
  const [dateRange, setDateRange] = useState<{ start_date: string; end_date: string }>(() => {
    const urlStart = searchParams.get('start_date');
    const urlEnd = searchParams.get('end_date');
    if (urlStart && urlEnd) {
      return { start_date: urlStart, end_date: urlEnd };
    }
    const now = new Date();
    return {
      start_date: `${now.getFullYear()}-01-01`,
      end_date: `${now.getFullYear()}-12-31`,
    };
  });

  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const f: Record<string, any> = {};
    for (const key of paymentFilterKeys) {
      const val = searchParams.get(key);
      if (val) f[key] = val;
    }
    return f;
  });

  const [sortField, setSortField] = useState(searchParams.get('sort_field') || 'txn_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    (searchParams.get('sort_direction') as 'asc' | 'desc') || 'desc'
  );

  // URL sync. 'all' mode is exclusive — never write start/end alongside it.
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('period', period);
    if (period !== 'all') {
      if (dateRange.start_date) params.set('start_date', dateRange.start_date);
      if (dateRange.end_date) params.set('end_date', dateRange.end_date);
    }
    for (const key of paymentFilterKeys) {
      if (filters[key]) params.set(key, filters[key]);
    }
    if (sortField !== 'txn_date') params.set('sort_field', sortField);
    if (sortDir !== 'desc') params.set('sort_direction', sortDir);
    setSearchParams(params, { replace: true });
  }, [dateRange, filters, period, sortField, sortDir, setSearchParams]);

  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);
  const [customerModalId, setCustomerModalId] = useState<string | null>(null);
  const [paymentModalId, setPaymentModalId] = useState<string | null>(null);

  const handleDateRangeChange = useCallback(
    (range: { start_date: string; end_date: string }, p: string) => {
      setDateRange(range);
      setPeriod(p);
    },
    []
  );
  const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDir(direction);
  }, []);

  return (
    <DashboardLayout title="Invoice Payments" scrollable>
      <PaymentsTable
        initialDateRange={dateRange}
        initialPeriod={period}
        initialFilters={filters}
        initialSort={{ field: sortField, direction: sortDir }}
        onDateRangeChange={handleDateRangeChange}
        onFiltersChange={setFilters}
        onSortChange={handleSortChange}
        onRowClick={(p) => p.id && setPaymentModalId(p.id)}
        onOpenInvoice={setInvoiceModalId}
        onOpenCustomer={setCustomerModalId}
        preferencesKey="payments"
        userId={user?.id}
        userPreferences={userData?.preferences}
      />

      <InvoiceDetailModal
        show={!!invoiceModalId}
        onHide={() => setInvoiceModalId(null)}
        invoiceId={invoiceModalId}
      />

      <CustomerDetailModal
        show={!!customerModalId}
        onHide={() => setCustomerModalId(null)}
        customerId={customerModalId}
      />

      {paymentModalId && (
        <PaymentDetailModal
          paymentId={paymentModalId}
          onClose={() => setPaymentModalId(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['invoice-payments'] })}
        />
      )}
    </DashboardLayout>
  );
};

export default PaymentsPage;
